/**
 * financials.ts — the money reads and writes of the setup page.
 *
 * MOVED VERBATIM out of `FinancialsTab` and `useContractSetup`: same keys, same selects,
 * same validation sentences, same order of operations. The mutation bodies live here as
 * plain async functions; the `useMutation` wrappers — and therefore every toast and every
 * invalidation — stay exactly where they were, so nothing about what a save does or reports
 * has moved.
 *
 * `['deposit-payments', contractId]` LIVED HERE AND IS GONE. It had two query functions
 * registered against it — this page's and the setup hook's — which is the hazard that broke the
 * insurance loan insert, and it was the third read of one table besides the signing board's and
 * the deposit block's. All three now share `setup/data/deposits.ts`, whose superset row carries
 * the transaction, the refunds and the legacy agreement arc together. There is one fetcher per
 * key in this module and that is the point of it.
 */

import { supabase } from '@/integrations/supabase/client';

export const contractInstalmentKey = (contractId: number) =>
    ['contract-instalment', contractId] as const;
export const unassignedDepositsKey = ['unassigned-deposits'] as const;

export interface UnassignedDeposit {
    id: number;
    transaction_id: number;
    remarks: string | null;
    created_at: string;
    transactions: {
        amount: number;
        transaction_at: string;
        description: string | null;
    } | null;
}

// ─── Reads ───────────────────────────────────────────────────────────────────

/** The pool a deposit can be linked from: every deposit that belongs to no contract. */
export async function fetchUnassignedDeposits(): Promise<UnassignedDeposit[]> {
    const { data, error } = await supabase
        .from('contract_deposits')
        .select(`
            id,
            transaction_id,
            remarks,
            created_at,
            transactions (
                amount,
                transaction_at,
                description
            )
        `)
        .is('contract_id', null)
        .order('created_at', { ascending: false });
    if (error) throw error;
    return data as unknown as UnassignedDeposit[];
}

/** The contract's weekly instalment — the first one, which is the only one setup writes. */
export async function fetchContractInstalment(contractId: number) {
    const { data, error } = await supabase
        .from('contract_instalments')
        .select('*')
        .eq('contract_id', contractId)
        .order('created_at', { ascending: true })
        .limit(1)
        .maybeSingle();
    if (error) throw error;
    return data;
}

// ─── The Money's refusals ────────────────────────────────────────────────────

/**
 * The three refusals of the Money particulars, as predicates: the sentence a figure earns, or null.
 *
 * ONE DEFINITION, BECAUSE THE SENTENCE IS THE KEY. `CLAUSE_REFUSALS` attributes a refusal to the
 * field it is about by matching the sentence EXACTLY (`data/refusalFields.ts`), so a second copy of
 * "Enter a valid sale price" written a word apart lands under no field at all.
 *
 * THEY ARE SEPARATELY CALLABLE BECAUSE THE WRITE IS NO LONGER FIRST [D-B42]. A money change on a
 * contract whose hire purchase document is signed voids that document BEFORE it writes, so "the
 * write refuses a typo before anything is spent" stopped being free — `../moneyCommit` buys it back
 * by running all three of these before the first void.
 */
export function salePriceRefusal(soldAt: string): string | null {
    const value = parseFloat(soldAt);
    return isNaN(value) || value <= 0 ? 'Enter a valid sale price' : null;
}

/** Blank is a real answer here and clears the column — see `saveCashPrice`. */
export function cashPriceRefusal(cashPrice: string): string | null {
    const typed = cashPrice.trim();
    if (typed === '') return null;
    const value = parseFloat(typed);
    return isNaN(value) || value <= 0 ? 'Enter a valid cash price' : null;
}

/** Both halves of the Weekly Instalment row, in the order the clause reads them. */
export function instalmentRefusal({
    amount,
    startsAt,
}: {
    amount: string;
    startsAt: string;
}): string | null {
    const value = parseFloat(amount);
    if (isNaN(value) || value <= 0) return 'Enter a valid instalment amount';
    if (!startsAt) return 'Select a start date';
    return null;
}

// ─── Writes ──────────────────────────────────────────────────────────────────

/**
 * The sale price of the vehicle. Prints as the Hire-purchase price in the hire purchase Schedule's
 * "The Money" particulars.
 */
export async function saveSalePrice(contractId: number, soldAt: string) {
    const refusal = salePriceRefusal(soldAt);
    if (refusal) throw new Error(refusal);

    const { error } = await supabase
        .from('contracts')
        .update({ sold_at: soldAt as any })
        .eq('id', contractId);
    if (error) throw error;
}

/**
 * The cash price — what the Vehicle could have been bought outright for. The Schedule's FIRST
 * money row, one above the hire-purchase price.
 *
 * IT IS TYPED AND NEVER DERIVED, which is the whole reason it is a column and a field rather than
 * a calculation. `sold_at` is the hire-purchase price; the DIFFERENCE between the two is the charge
 * for credit that NRCD 292 s.3 wants stated, so resolving one from the other would print the two
 * prices equal and state on a signed page that this contract carries no charge for credit at all.
 * `agreement_merge_data` reads the column straight (`20260816010000`) and nothing anywhere derives
 * it.
 *
 * BLANK CLEARS IT, WHICH IS THE OPPOSITE OF `saveOverdueRate` AND FOR THE OPPOSITE REASON. The
 * column is nullable with no default, deliberately: a contract that has not stated a cash price
 * yet renders a fill-in the merge pre-flight reports, rather than a fabricated figure. So an empty
 * field is a real answer — "not stated" — and writing NULL from it is how an admin takes back a
 * figure they entered against the wrong contract.
 *
 * THE FLOOR IS `> 0`, THE SALE PRICE'S FLOOR, NOT THE RATE'S. Zero per cent is a rate seventeen
 * live contracts genuinely carry; a vehicle with a cash price of nothing does not exist, and a
 * stored zero would both print GH₵0.00 on the Schedule and satisfy the readiness gate that exists
 * to make the price be stated.
 */
export async function saveCashPrice(contractId: number, cashPrice: string) {
    const refusal = cashPriceRefusal(cashPrice);
    if (refusal) throw new Error(refusal);

    const typed = cashPrice.trim();
    const value: number | null = typed === '' ? null : parseFloat(typed);

    const { error } = await supabase
        .from('contracts')
        .update({ cash_price: value })
        .eq('id', contractId);
    if (error) throw error;
}

/**
 * The overdue interest rate, in per cent per annum. Prints on three instruments.
 *
 * IT REFUSES AN EMPTY FIELD RATHER THAN CLEARING THE COLUMN. `contracts.overdue_interest_rate` is
 * NOT NULL (`20260801080000`) with DEFAULT 365 (`20260822010000`), so there is no such thing as
 * "no rate": a blank field is somebody midway through typing, and writing NULL from it would be
 * refused by Postgres with a constraint sentence instead of the one an admin can act on.
 *
 * ZERO IS VALID AND IS NOT AN ACCIDENT — seventeen live contracts carry 0%. The floor is therefore
 * `>= 0` rather than `> 0`, and the only other refusal is a figure that is not a number.
 */
export async function saveOverdueRate(contractId: number, overdueRate: string) {
    const typed = overdueRate.trim();
    if (typed === '') throw new Error('The overdue interest rate cannot be blank — it prints on clause 2.3');

    const value = parseFloat(typed);
    if (isNaN(value) || value < 0) throw new Error('Enter a valid overdue interest rate');

    const { error } = await supabase
        .from('contracts')
        .update({ overdue_interest_rate: value })
        .eq('id', contractId);
    if (error) throw error;
}

/** Internal only — the partner split. Empty clears it. */
export async function saveManagementRate(contractId: number, managementRate: string) {
    const value = managementRate.trim() === '' ? null : parseFloat(managementRate);
    if (value !== null && (isNaN(value) || value < 0 || value > 100)) {
        throw new Error('Enter a valid percentage (0-100)');
    }

    const { error } = await supabase
        .from('contracts')
        .update({ management_rate: value })
        .eq('id', contractId);
    if (error) throw error;
}

/**
 * Link and unlink deposits in one commit.
 *
 * UNLINKS RUN FIRST, deliberately: an admin swapping one deposit for another would otherwise
 * be able to leave both attached if the second loop threw.
 */
export async function saveDepositLinks({
    contractId,
    depositsToLink,
    depositsToUnlink,
}: {
    contractId: number;
    depositsToLink: number[];
    depositsToUnlink: number[];
}) {
    for (const depositId of depositsToUnlink) {
        const { error } = await supabase
            .from('contract_deposits')
            .update({ contract_id: null })
            .eq('id', depositId);
        if (error) throw error;
    }

    for (const depositId of depositsToLink) {
        const { error } = await supabase
            .from('contract_deposits')
            .update({ contract_id: contractId })
            .eq('id', depositId);
        if (error) throw error;
    }
}

/**
 * The weekly instalment and the date it starts. The amount prints as the Weekly Instalment
 * particulars row; the start date feeds the Schedule's derived Expected completion.
 */
export async function saveContractInstalment({
    contractId,
    existingInstalmentId,
    amount: amountInput,
    startsAt,
}: {
    contractId: number;
    /** The row to update; null creates one. */
    existingInstalmentId: number | null;
    amount: string;
    startsAt: string;
}) {
    const refusal = instalmentRefusal({ amount: amountInput, startsAt });
    if (refusal) throw new Error(refusal);

    const amount = parseFloat(amountInput);

    if (existingInstalmentId != null) {
        const { error } = await supabase
            .from('contract_instalments')
            .update({ amount, starts_at: startsAt })
            .eq('id', existingInstalmentId);

        // MOVING A ROW ONTO A DATE ANOTHER ROW ALREADY HOLDS. `UNIQUE(contract_id, starts_at)`
        // refuses it, and it must: which of the two amounts is in force that week has no answer,
        // and the eleven functions reading this table would each pick one their own way. The
        // constraint name means nothing to an admin, so say what was actually asked for.
        if (error?.code === '23505') {
            throw new Error(
                `This contract already has a weekly instalment starting ${startsAt}. ` +
                'Edit that row instead, or choose a different start date.',
            );
        }
        if (error) throw error;
    } else {
        const { error } = await supabase
            .from('contract_instalments')
            .insert({ contract_id: contractId, amount, starts_at: startsAt });
        if (!error) return;

        // ADOPT, DO NOT DUPLICATE. `existingInstalmentId` is read from state loaded before this
        // save began, so two overlapping saves can both arrive holding null and both insert. The
        // week already exists: write this amount into it. (The same race put two insurance loans
        // on contract #318 and billed the insurance week twice.)
        if (error.code !== '23505') throw error;

        const { error: adoptError } = await supabase
            .from('contract_instalments')
            .update({ amount })
            .eq('contract_id', contractId)
            .eq('starts_at', startsAt);
        if (adoptError) throw adoptError;
    }
}
